import { RefCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { PillBannerButton, type PillBannerButtonProps } from "components/footer/PillBannerButton";

const AUTO_ACTION_DURATION = 6000;

export type AutoActionButtonProps<T extends React.ElementType = "button"> = Omit<
  PillBannerButtonProps<T>,
  "kind" | "style"
> & {
  /** Text to display in the button */
  children: React.ReactNode;
  /** Called when the countdown completes (before auto-click) */
  onAutoActionComplete?: () => void;
  ref?: RefCallback<HTMLElement>;
};

/**
 * A PillBannerButton with a 6-second countdown animation that auto-clicks when complete.
 * Should only be used inside a PillBanner.
 *
 * The button has an animated background that fills from left to right over 6 seconds,
 * with text that changes color as the fill progresses.
 *
 * The animation only starts when the tab has focus. If the tab is in the background,
 * it waits until focus is gained before starting.
 */
export const AutoActionButton = <T extends React.ElementType = "button">({
  as,
  ref: outerRef,
  className,
  onAutoActionComplete,
  onClick,
  children,
  ...rest
}: AutoActionButtonProps<T>) => {
  const innerRef = useRef<HTMLElement | null>(null);
  const [wasClicked, setWasClicked] = useState(false);
  const [isTabFocused, setIsTabFocused] = useState(() => document.hasFocus());
  const elapsedTimeRef = useRef(0);
  const lastFocusTimeRef = useRef<number | null>(null);

  // Track tab focus/blur and accumulate elapsed time
  useEffect(() => {
    const handleFocus = () => {
      lastFocusTimeRef.current = Date.now();
      setIsTabFocused(true);
    };
    const handleBlur = () => {
      // Accumulate time spent focused
      if (lastFocusTimeRef.current !== null) {
        elapsedTimeRef.current += Date.now() - lastFocusTimeRef.current;
        lastFocusTimeRef.current = null;
      }
      setIsTabFocused(false);
    };

    // Initialize lastFocusTime if tab is already focused
    if (document.hasFocus()) {
      lastFocusTimeRef.current = Date.now();
    }

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  // Auto-click after animation completes if not already clicked
  // Only run timer when tab is focused, using remaining time
  useEffect(() => {
    if (wasClicked || !isTabFocused) return;

    const remainingTime = Math.max(0, AUTO_ACTION_DURATION - elapsedTimeRef.current);

    const timeoutId = setTimeout(() => {
      onAutoActionComplete?.();
      const el = innerRef.current;
      if (el) {
        el.click();
      }
    }, remainingTime);

    return () => clearTimeout(timeoutId);
    // react-doctor-disable-next-line react-doctor/prefer-use-effect-event
  }, [wasClicked, isTabFocused, onAutoActionComplete]);

  const handleClick = (e: React.MouseEvent) => {
    setWasClicked(true);
    onClick?.(e);
  };

  // Pause animation when tab is not focused
  const animationStyle = {
    animationPlayState: isTabFocused ? "running" : "paused",
  } as const;

  return (
    <PillBannerButton
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      as={as as any}
      className={twMerge("animate-follow-button border border-green", className)}
      ref={(el: HTMLElement | null) => {
        outerRef?.(el);
        innerRef.current = el;
      }}
      style={{
        background: "linear-gradient(to right, white 50%, var(--green) 50%)",
        backgroundSize: "200% 100%",
        ...animationStyle,
      }}
      {...rest}
      onClick={handleClick}
    >
      {/* Extend span to cover full button area so text gradient matches button gradient exactly */}
      <span
        className="-mx-3 animate-follow-button bg-clip-text px-3"
        style={{
          background: "linear-gradient(to right, var(--black-tint-20) 50%, white 50%)",
          backgroundSize: "200% 100%",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          ...animationStyle,
        }}
      >
        {children}
      </span>
    </PillBannerButton>
  );
};
